import {
  getCopyContent,
  getReportHtmlSource,
  normalizeReportHtmlForExport,
} from "./contentProcessor";

const BLOCK_TAGS = new Set([
  "P",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "BLOCKQUOTE",
  "PRE",
]);

const CRE_REPORT_ROOT = "[data-cre-report-root]";
const CRE_REPORT_HTML_ATTR = "data-cre-report-html";

function normalizeText(text) {
  return text.replace(/\u00a0/g, " ");
}

function elementText(el) {
  return normalizeText(el.innerText || el.textContent || "").trimEnd();
}

function paragraphContentText(el) {
  if (!(el instanceof HTMLElement)) return "";
  if (el.classList.contains("draggable-paragraph-content")) {
    return elementText(el);
  }
  if (el.classList.contains("draggable-paragraph")) {
    const content = el.querySelector(":scope > .draggable-paragraph-content");
    return content ? elementText(content) : elementText(el);
  }
  return "";
}

function listItemOwnText(li) {
  const clone = li.cloneNode(true);
  clone
    .querySelectorAll("ul, ol, label, input, button, [data-drag-handle], svg")
    .forEach((el) => el.remove());
  return elementText(clone);
}

function listItems(listEl) {
  const scoped = listEl.querySelectorAll(":scope > li");
  if (scoped.length > 0) return Array.from(scoped);
  return Array.from(listEl.children).filter((c) => c.tagName === "LI");
}

function isTaskList(listEl) {
  return listEl.getAttribute("data-type") === "taskList";
}

function taskItemPrefix(li) {
  const input = li.querySelector('input[type="checkbox"]');
  const checked =
    li.getAttribute("data-checked") === "true" ||
    (input instanceof HTMLInputElement && input.checked);
  return checked ? "[x] " : "[ ] ";
}

function appendListLines(listEl, lines, processed = new WeakSet()) {
  if (!(listEl instanceof HTMLElement) || processed.has(listEl)) return;
  processed.add(listEl);

  const isOrdered = listEl.tagName === "OL";
  const taskList = isTaskList(listEl);
  const items = listItems(listEl);

  items.forEach((li, index) => {
    let text = listItemOwnText(li);
    if (taskList && text) {
      text = taskItemPrefix(li) + text;
    } else if (isOrdered && text && !/^\d+[.)]\s/.test(text)) {
      const start = Number.parseInt(listEl.getAttribute("start") || "1", 10);
      const n = Number.isFinite(start) ? start + index : index + 1;
      text = `${n}. ${text}`;
    }
    if (text) lines.push(text);

    li.querySelectorAll("ul, ol").forEach((nested) => {
      appendListLines(nested, lines, processed);
    });
  });
}

function tableRows(table) {
  return Array.from(table.querySelectorAll("tr"));
}

function rowCells(tr) {
  return Array.from(tr.querySelectorAll("th, td")).map((cell) =>
    elementText(cell),
  );
}

function appendTableLines(table, lines) {
  const rows = tableRows(table);
  if (rows.length === 0) {
    const text = elementText(table);
    if (text) lines.push(text);
    return;
  }
  rows.forEach((tr) => {
    const cells = rowCells(tr);
    if (cells.some((c) => c.length > 0)) {
      lines.push(cells.join("\t"));
    }
  });
}

function tableInElement(el) {
  if (!(el instanceof HTMLElement)) return null;
  if (el.tagName === "TABLE") return el;
  const direct = el.querySelector(":scope > table");
  return direct instanceof HTMLTableElement ? direct : null;
}

function isSkippedMediaBlock(el) {
  const tag = el.tagName;
  return (
    tag === "IMG" ||
    tag === "FIGURE" ||
    el.classList.contains("resizable-image-wrapper")
  );
}

function walkBlocks(parent, lines) {
  for (const child of parent.children) {
    if (!(child instanceof HTMLElement)) continue;
    const tag = child.tagName;

    if (isSkippedMediaBlock(child)) {
      continue;
    }
    if (tag === "UL" || tag === "OL") {
      appendListLines(child, lines);
    } else if (tag === "TABLE") {
      appendTableLines(child, lines);
    } else if (tag === "HR") {
      lines.push("");
    } else if (child.classList.contains("draggable-paragraph")) {
      const text = paragraphContentText(child);
      if (text) lines.push(text);
    } else if (child.classList.contains("draggable-paragraph-content")) {
      const text = elementText(child);
      if (text) lines.push(text);
    } else if (BLOCK_TAGS.has(tag)) {
      const text = elementText(child);
      if (text) lines.push(text);
    } else if (tag === "DIV" || tag === "SECTION" || tag === "ARTICLE") {
      const table = tableInElement(child);
      if (table) {
        appendTableLines(table, lines);
      } else {
        walkBlocks(child, lines);
      }
    } else {
      walkBlocks(child, lines);
    }
  }
}

function blocksToPlainText(root) {
  if (!root) return "";
  const host =
    root instanceof HTMLElement ? root : root.body instanceof HTMLElement ? root.body : null;
  if (!host) return "";

  const lines = [];
  walkBlocks(host, lines);
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

function htmlToReportPlainText(html) {
  if (!html?.trim()) return "";
  const normalized = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p/gi, "</p>\n<p")
    .replace(/<\/h([1-6])>\s*</gi, "</h$1>\n<")
    .replace(/<\/li>\s*<li/gi, "</li>\n<li")
    .replace(/<\/tr>\s*<tr/gi, "</tr>\n<tr")
    .replace(/<\/table>\s*</gi, "</table>\n<");
  const doc = new DOMParser().parseFromString(normalized, "text/html");
  return blocksToPlainText(doc.body);
}

function pickPlainText({ html, prose }) {
  const fromHtml = html ? htmlToReportPlainText(html) : "";
  const fromDom = prose ? blocksToPlainText(prose) : "";
  if (!fromHtml) return fromDom;
  if (!fromDom) return fromHtml;
  return fromHtml.length >= fromDom.length ? fromHtml : fromDom;
}

function findProseMirrorRoot(rteRef) {
  const fromRef = rteRef?.current?.querySelector?.(".ProseMirror");
  if (fromRef instanceof HTMLElement) return fromRef;
  const fromPage = document.querySelector(".rte-editor-scroll .ProseMirror");
  return fromPage instanceof HTMLElement ? fromPage : null;
}

function findCreReportRoot() {
  const el = document.querySelector(CRE_REPORT_ROOT);
  return el instanceof HTMLElement ? el : null;
}

/**
 * Writes cleaned report HTML to `data-cre-report-html` on `[data-cre-report-root]`.
 * Used by `useCreReportHtmlSync` (page) and read by the CRE content script for PiP copy.
 */
export function syncCreReportHtmlAttribute(editor) {
  const root = findCreReportRoot();
  if (!root) return;
  const raw = normalizeReportHtmlForExport(editor?.getHTML?.() || "");
  if (!raw?.trim()) {
    root.removeAttribute(CRE_REPORT_HTML_ATTR);
    return;
  }
  root.setAttribute(CRE_REPORT_HTML_ATTR, getCopyContent(raw));
}

function readCreReportHtmlAttribute() {
  const root = findCreReportRoot();
  return root?.getAttribute(CRE_REPORT_HTML_ATTR)?.trim() ?? "";
}

function htmlWithoutImages(html) {
  if (!html?.trim()) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc
    .querySelectorAll("img, figure, .resizable-image-wrapper")
    .forEach((node) => node.remove());
  return doc.body.innerHTML;
}

export function getReportClipboardPayload({ editor, transcript, rteRef }) {
  const raw = normalizeReportHtmlForExport(
    getReportHtmlSource({ editor, transcript }),
  );
  let html = raw ? getCopyContent(raw) : "";
  html = htmlWithoutImages(html);

  const prose = findProseMirrorRoot(rteRef);
  const attrHtml = htmlWithoutImages(readCreReportHtmlAttribute());
  const plainText = pickPlainText({
    html: html || attrHtml,
    prose,
  });

  if (!plainText.trim() && !html.trim()) {
    return { html: "", plainText: "" };
  }

  return { html, plainText };
}

export async function writeReportToClipboard({ html, plainText }) {
  const plain = (plainText ?? htmlToReportPlainText(html)).trim();
  const htmlClean = html?.trim() ?? "";
  if (!plain && !htmlClean) {
    throw new Error("Nothing to copy");
  }

  if (
    htmlClean &&
    typeof ClipboardItem !== "undefined" &&
    navigator.clipboard?.write
  ) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([htmlClean], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch {
      /* fallback */
    }
  }

  await navigator.clipboard.writeText(plain);
}
